import env from './env';
import { consentHeaderValue } from './cookieConsent';

const apiBaseUrl = (env.get('VITE_API_BASE_URL') || '').replace(/\/$/, '');

function detectRegionHeader(): Record<string, string> {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) return { 'X-Timezone': tz };
  } catch {
    // Intl API unavailable — omit the header
  }
  return {};
}

/**
 * Wrapper around fetch that prepends the API base URL to relative paths.
 * Use this instead of fetch('/api/...') for all backend API calls.
 */
export function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (typeof input === 'string' && input.startsWith('/')) {
    input = `${apiBaseUrl}${input}`;
  }
  const mergedInit: RequestInit = {
    ...init,
    headers: {
      ...detectRegionHeader(),
      // DPDP consent signal — lets the backend skip analytics event recording
      // (click/impression ip_hash tracking) when consent is denied or pending.
      'X-Analytics-Consent': consentHeaderValue(),
      ...(init?.headers ?? {}), // caller headers take precedence
    },
  };
  return fetch(input, mergedInit);
}

/**
 * GET-only fetch with silent exponential-backoff retries on 5xx or network errors.
 * Do NOT use for POST/PUT/DELETE — retrying mutations can cause duplicate side-effects.
 *
 * Default: up to 2 retries (3 total attempts), starting at 600 ms delay, doubling each time.
 */
export async function apiFetchWithRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
  options: { retries?: number; baseDelayMs?: number } = {}
): Promise<Response> {
  const { retries = 2, baseDelayMs = 600 } = options;

  let lastResponse: Response | undefined;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await apiFetch(input, init);

      // Success or a client error (4xx) — don't retry, return as-is.
      if (response.status < 500) return response;

      lastResponse = response;
    } catch {
      // Network-level failure (no response at all); fall through to retry.
    }

    if (attempt < retries) {
      await new Promise(r => setTimeout(r, baseDelayMs * 2 ** attempt));
    }
  }

  // All attempts exhausted — return the last bad response if we have one,
  // otherwise throw a network error.
  if (lastResponse) return lastResponse;
  throw new Error('Network error — request failed after retries');
}
